import { Column, ColumnConfig } from './column';
import { ColumnType } from './column-type.enum';
import { DisplayColumn } from './patterns/display-column';
import { FallbackColumn, FallbackColumnConfig } from './patterns/fallback-column';

export enum TextColumnFormat {
  NoFormat,
  TitleCase,
  Date,
  Link,
}

export interface TextColumnConfig<T> extends ColumnConfig, FallbackColumnConfig {
  format?: TextColumnFormat;
  routerLink?: (elem: T) => string[];
}

export class TextColumn<T> extends Column<T> implements DisplayColumn<T>, FallbackColumn<T> {
  public readonly columnType = ColumnType.Text;
  public config: TextColumnConfig<T> = {
    format: TextColumnFormat.NoFormat,
    cellClasses: []
  };

  /**
   * Create a new table column.
   *
   * @param title The column title, to be displayed once along the top of the table
   * @param display A function that takes the element for a given row and returns the data to display for it in this column
   * @param config Extra configuration for this column
   */
  constructor(
    public readonly title: string,
    public readonly display: (row: T) => string | Date | number,
    public extraConfig?: TextColumnConfig<T>,
  ) {
    super();
    this.applyConfig(extraConfig);
  }

  public toString(): string {
    return `[TextColumn (title: ${this.title})]`;
  }

  public useFallback(row: T): boolean {
    const value = this.display(row);
    return typeof value === 'undefined' || value === null;
  }

  public get fallback(): string {
    if (this.config.fallback) {
      return this.config.fallback;
    }
    if (this.config.format === TextColumnFormat.Date) {
      return '(no date)';
    }
    return '(none)';
  }
}
